import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { TextField, Grid, Box, Button, IconButton, Typography, Autocomplete } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import useProduct from "hooks/useProduct";
import useCategory from "hooks/useCategory";
import imageCompression from "browser-image-compression";
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ProductForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get("id");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const { getProductById, createProduct, updateProduct } = useProduct();
  const { getCategoryOptions } = useCategory();

  const [formData, setFormData] = useState({
    name: "",
    images: [],
    category: "",
    amount: "",
    marketPrice: "",
    imagePreviews: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCategoryChange = (event, value) => {
    setFormData((prevState) => ({
      ...prevState,
      category: value ? value._id : null, // Store a single category ID
    }));
  };

  const handleImageChange = async (e) => {
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const maxImages = 4;
    const files = Array.from(e.target.files);
    let validFiles = [];

    // Get current number of images
    const currentImages = formData?.images || [];

    // Check if total images exceed the limit
    if (currentImages.length + files.length > maxImages) {
      alert(`You can only upload a maximum of ${maxImages} images.`);
      return;
    }

    for (const file of files) {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large! Compressing it...`);

        // Compression options
        const options = {
          maxSizeMB: 1, // Target max file size of 1MB
          maxWidthOrHeight: 1024, // Resize if too large
          useWebWorker: true,
        };

        try {
          const compressedFile = await imageCompression(file, options);
          validFiles.push(compressedFile);
        } catch (error) {
          console.error("Image compression failed:", error);
        }
      } else {
        validFiles.push(file);
      }

      // Stop adding images if the limit is reached
      if (currentImages.length + validFiles.length >= maxImages) {
        break;
      }
    }

    if (validFiles.length === 0) return; // Stop if no valid files remain

    setFormData((prevState) => ({
      ...prevState,
      images: [...currentImages, ...validFiles].slice(0, maxImages), // Ensure only up to maxImages are added
      imagePreviews: [
        ...(prevState?.imagePreviews || []),
        ...validFiles.map((file) => URL.createObjectURL(file)),
      ].slice(0, maxImages),
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData((prevState) => {
      const newImages = [...prevState.images];
      const newPreviews = [...prevState.imagePreviews];
      newImages.splice(index, 1);
      newPreviews.splice(index, 1);

      return {
        ...prevState,
        images: newImages,
        imagePreviews: newPreviews,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.category || !formData.amount || !formData.marketPrice) {
      alert("Please fill all required fields.");
      return;
    }

    const productData = new FormData();
    productData.append("name", formData.name);
    productData.append("category", formData.category);
    productData.append("amount", formData.amount);
    productData.append("marketPrice", formData.marketPrice);
    const imageFiles = [];
    const imageUrls = [];

    formData.images.forEach((image) => {
      if (typeof image === "string") {
        imageUrls.push(image); // Collect image URLs separately
      } else {
        imageFiles.push(image); // Collect file objects separately
      }
    });

    // Append file objects individually
    imageFiles.forEach((file) => {
      productData.append("images", file);
    });

    // Append URLs as an array (convert to JSON)
    if (imageUrls.length > 0) {
      productData.append("images", JSON.stringify(imageUrls));
    }
    if (productId) {
      const productUpdateResponse = await updateProduct(productId, productData);
      if (productUpdateResponse.success) {
        navigate("/product-management");
      } else {
        alert(productUpdateResponse.message);
      }
    } else {
      const response = await createProduct(productData);
      if (response.success) {
        navigate("/product-management");
      } else {
        alert(response.message);
      }
    }
  };

  const fetchCategoryList = async () => {
    const response = await getCategoryOptions();
    if (response.success) {
      setCategoryOptions(response.data);
    }
  };

  const fetchProduct = async () => {
    const response = await getProductById(productId);
    if (response?.success) {
      setFormData({
        name: response.data.name,
        category: response.data.category._id,
        amount: response.data.amount,
        marketPrice: response.data.marketPrice,
        imagePreviews: response.data.images || [],
        images: response.data.images || [],
      });
    }
  };

  useEffect(() => {
    fetchCategoryList();
    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  return (
    <Box
      sx={{
        width: "100%",
        background: "white",
        minHeight: "calc(100vh - 120px)",
        p: 4,
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <MDTypography variant="h5" component="h1" align="center" gutterBottom>
        Product Form
      </MDTypography>
      <MDTypography variant="caption" color="text" align="center" paragraph>
        Fill in the product details below to add a new product or update an existing one.
      </MDTypography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Name
          </MDTypography>
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Category
          </MDTypography>
          <Autocomplete
            name="category"
            value={categoryOptions.find((category) => category._id === formData?.category) || null}
            onChange={handleCategoryChange}
            id="single-category-select"
            options={categoryOptions}
            getOptionLabel={(option) => option?.name}
            renderOption={(props, option) => {
              const { ...optionProps } = props;
              return <li {...optionProps}>{option?.name}</li>;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 42,
                    padding: "8px 14px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "10px 0",
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Amount
          </MDTypography>
          <TextField
            fullWidth
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Market Price
          </MDTypography>
          <TextField
            fullWidth
            type="number"
            name="marketPrice"
            value={formData.marketPrice}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <MDTypography variant="body2" gutterBottom>
            Upload Images
          </MDTypography>
          {/* Upload Section */}
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
              "&:hover": { backgroundColor: "#f1f1f1" },
            }}
            onClick={() => document.getElementById("upload-images").click()}
          >
            <input
              type="file"
              multiple
              onChange={handleImageChange}
              accept="image/png, image/jpeg, image/jpg"
              id="upload-images"
              style={{ display: "none" }}
            />
            <Typography variant="body1" color="textSecondary">
              Drag & drop images here or click to select files
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Only JPG, JPEG, PNG files under 5MB are allowed. Maximum 4 images.
            </Typography>
          </Box>

          {/* Image Previews Section */}
          {formData.imagePreviews.length > 0 && (
            <Box mt={2}>
              <MDTypography variant="body2" gutterBottom>
                Selected Images
              </MDTypography>
              <Grid container spacing={2}>
                {formData.imagePreviews.map((src, index) => (
                  <Grid item key={index}>
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: 2,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f1f1f1",
                      }}
                    >
                      <img
                        src={src}
                        alt="preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(255,255,255,0.8)",
                          "&:hover": { backgroundColor: "rgba(255,0,0,0.8)", color: "#fff" },
                        }}
                        onClick={() => handleRemoveImage(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <MDButton type="submit" variant="contained" color="dark" fullWidth onClick={handleSubmit}>
            Submit
          </MDButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductForm;
